import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Container, Paper, CircularProgress, Alert, TextField, Button, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip, Drawer } from '@mui/material';
import { Autocomplete } from '@mui/material';
import useDebounce from '../../../hooks/useDebounce';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';
import { X, ExternalLink, RefreshCw, Plus, Upload, Mic, ChevronDown, MapPin, Award, Download, CheckCircle, Clock, Calendar, XCircle, AlertCircle, Sliders, HelpCircle, Filter, Briefcase } from 'lucide-react';
import api, { getSemanticRanking } from '../../../api/client';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import BulkScheduleProgressModal from '../component/BulkScheduleProgressModal';
import BulkScheduleFollowUp from './BulkScheduleFollowUp';
import BulkScheduleJdSelect, { BulkScheduleJdOption, jdAutocompleteListboxSx, jdAutocompletePaperProps } from './BulkScheduleJdSelect';
import AddJobDescriptionDialog from '../component/AddJobDescriptionDialog';
import { saveJdBenchmarks, splitJdFormAndBenchmarks } from './jobDescription/benchmarkLabels';
import JDFromAudioModal from './jobDescription/JDFromAudioModal';
import AddDepartmentModal from '../component/AddDepartmentModal';
import Pagination from '../component/Pagination';
import PremiumDatePicker from '../component/PremiumDatePicker';
import PageHeader, { PageCard, PageHeaderButton, PageShell } from '../../common/PageHeader';
import AptiviewLoader from '../../common/AptiviewLoader/AptiviewLoader';
import { usePageLoading } from '../../../contexts/LoadingContext';
import { convertMarkdownToHtml } from '../../../utils/markdownToHTML';
import * as XLSX from 'xlsx';

const FONT = { fontFamily: "'Poppins', sans-serif" };
const GRADIENT = 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)';
const outlinedInputSx = {
  borderRadius: '8px',
  fontFamily: FONT.fontFamily,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e2e8f0',
    borderWidth: '1.5px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#cbd5e1',
    borderWidth: '1.5px',
  },
  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ef4444 !important',
  },
  '&.Mui-focused': {
    outline: 'none',
  },
  '& input': {
    outline: 'none',
  },
};
const buttonSx = {
  ...FONT,
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
};
const paperSx = {
  p: { xs: 1.5, sm: 2 },
  mb: 2,
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
  bgcolor: '#fff',
  flexShrink: 0,
};
const dataGridSx = {
  ...FONT,
  border: 'none',
  borderRadius: 0,
  '& .MuiDataGrid-columnHeaders': {
    ...FONT,
    backgroundColor: '#f8fafc',
    color: '#475569',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid #e2e8f0',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  '& .MuiDataGrid-cell': {
    ...FONT,
    fontSize: '0.8125rem',
    color: '#1e293b',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiDataGrid-row:nth-of-type(even)': {
    backgroundColor: '#fafbfc',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(239, 246, 255, 0.6) !important',
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiCheckbox-root': {
    color: '#cbd5e1',
    p: 0.75,
  },
  '& .MuiCheckbox-root.Mui-checked': {
    color: '#0ea5e9',
  },
  '& .MuiDataGrid-footerContainer': {
    display: 'none',
  },
};

const sectionTitleSx = {
  ...FONT,
  fontWeight: 600,
  fontSize: '0.8125rem',
  color: '#334155',
  mb: 1,
};

const SkillToggleChip = ({ skill, active, onToggle, idleBg, idleColor, idleBorder, activeBg, activeBorder }) => (
  <button
    type="button"
    onClick={() => onToggle(skill)}
    style={{
      fontFamily: FONT.fontFamily,
      background: active ? activeBg : idleBg,
      color: active ? '#fff' : idleColor,
      border: `1px solid ${active ? activeBorder : idleBorder}`,
    }}
    className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-xl cursor-pointer transition-all shadow-none"
  >
    {skill}
  </button>
);

const Step = ({ number, title, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25, flexWrap: 'wrap', gap: 1, flexShrink: 0, ...FONT }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '8px',
          background: 'rgba(14, 165, 233, 0.12)',
          color: '#0284c7',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: '0.75rem',
          ...FONT,
        }}
      >
        {number}
      </Box>
      <Typography sx={{ ...FONT, fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>
        {title}
      </Typography>
    </Box>
    {action && <Box>{action}</Box>}
  </Box>
);

const BulkSchedulePage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = document.documentElement.dir === 'rtl' || i18n?.language === 'ar';
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedJd, setSelectedJd] = useState('');
  const [showFocusAreaDrawer, setShowFocusAreaDrawer] = useState(false);
  const [activeTab, setActiveTab] = useState('schedule');

  // Dropdown & JD Modal States
  const [anchorEl, setAnchorEl] = useState(null);
  const dropdownOpen = Boolean(anchorEl);

  const initialFormState = {
    title: "", department: "", status: "open", employment_type: "", experience_level: "",
    required_skills: [], nice_to_have_skills: [], salary_range_min: "", salary_range_max: "",
    description: "", is_public: false, interview_style: "default", campus_questions_per_category: 3,
    questions: [], work_type: "",
    benchmarks: [], benchmarks_approve: false,
  };

  const [showJobDescriptionModal, setShowJobDescriptionModal] = useState(false);
  const [jobDescriptionForm, setJobDescriptionForm] = useState(initialFormState);
  const [jobDescriptionError, setJobDescriptionError] = useState("");
  const [uploadingJD, setUploadingJD] = useState(false);
  const fileInputRef = useRef(null);

  const [showAudioModal, setShowAudioModal] = useState(false);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [departmentError, setDepartmentError] = useState("");
  const [departmentSuccess, setDepartmentSuccess] = useState("");

  const handleDropdownClick = (event) => setAnchorEl(event.currentTarget);
  const handleDropdownClose = () => setAnchorEl(null);

  // Fetch departments when showJobDescriptionModal opens
  useEffect(() => {
    if (showJobDescriptionModal && departments.length === 0) {
      api.get("departments/").then(res => setDepartments(Array.isArray(res.data) ? res.data : res.data.results || [])).catch(() => setDepartments([]));
    }
  }, [showJobDescriptionModal, departments.length]);


  // Handle jd_id from query params and candidateIds from location state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jdIdFromUrl = params.get('jd_id');
    if (jdIdFromUrl) {
      const parsedId = /^\d+$/.test(jdIdFromUrl) ? parseInt(jdIdFromUrl, 10) : jdIdFromUrl;
      setSelectedJd(parsedId);
    }

    if (location.state?.candidateIds) {
      setSelectedCandidateIds(location.state.candidateIds);
      toast.info(t('bulkSchedule.candidatesPreselected', 'Candidates from analysis pre-selected.'));
    }

    if (location.state?.taskId) {
      setTaskId(location.state.taskId);
      setIsProgressModalOpen(true);
      // Clear the state so it doesn't re-trigger on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.search, location.state, t]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms debounce
  const [jdOptions, setJdOptions] = useState([]);
  const [loadingJds, setLoadingJds] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  usePageLoading(loadingCandidates && activeTab === 'schedule', {
    message: t('bulkSchedule.loadingCandidates', 'Loading candidates...'),
  });
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const rowSelectionModel = useMemo(() => ({
    type: 'include',
    ids: new Set(selectedCandidateIds)
  }), [selectedCandidateIds]);
  const [error, setError] = useState('');
  const [requiredSkills, setRequiredSkills] = useState([]);
  const [niceToHaveSkills, setNiceToHaveSkills] = useState([]);
  const [manualSkillInput, setManualSkillInput] = useState('');
  const [focusSkills, setFocusSkills] = useState([]); // All skills to be used for interview
  const [maxNoOfQuestions, setMaxNoOfQuestions] = useState('');
  const [interviewDurationMinutes, setInterviewDurationMinutes] = useState('');
  const [selectedJdDetails, setSelectedJdDetails] = useState(null);
  const [companyInterviewSettings, setCompanyInterviewSettings] = useState(null);

  const selectedJdObject = useMemo(() => {
    return jdOptions.find(jd => String(jd.id) === String(selectedJd)) || null;
  }, [jdOptions, selectedJd]);

  const defaultQuestions = useMemo(() => {
    const targetJd = selectedJdDetails || selectedJdObject;

    // 1. Explicit max_no_of_questions on JD (if specifically overridden)
    if (targetJd?.max_no_of_questions != null && targetJd.max_no_of_questions !== '') {
      return Number(targetJd.max_no_of_questions);
    }
    // 2. Company global interview settings (from Interview Configuration)
    if (companyInterviewSettings?.max_no_of_questions != null && companyInterviewSettings.max_no_of_questions !== '') {
      return Number(companyInterviewSettings.max_no_of_questions);
    }
    // 3. If nothing is set in configuration, default is unlimited (null)
    return null;
  }, [selectedJdDetails, selectedJdObject, companyInterviewSettings]);

  const defaultDuration = useMemo(() => {
    const targetJd = selectedJdDetails || selectedJdObject;

    // 1. Explicit duration on JD
    if (targetJd?.interview_duration_minutes != null && targetJd.interview_duration_minutes !== '') {
      return Number(targetJd.interview_duration_minutes);
    }
    if (targetJd?.interview_duration != null && targetJd.interview_duration !== '') {
      return Number(targetJd.interview_duration);
    }
    if (targetJd?.duration != null && targetJd.duration !== '') {
      return Number(targetJd.duration);
    }
    // 2. Company global interview settings
    if (companyInterviewSettings?.interview_duration_minutes != null && companyInterviewSettings.interview_duration_minutes !== '') {
      return Number(companyInterviewSettings.interview_duration_minutes);
    }
    // 3. Default fallback
    return 30;
  }, [selectedJdDetails, selectedJdObject, companyInterviewSettings]);

  const [scheduling, setScheduling] = useState(false); // State for scheduling process
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [gridKey, setGridKey] = useState(0); // Add key to force DataGrid remount on meaningful changes
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [candidateAddedFrom, setCandidateAddedFrom] = useState('');
  const [candidateAddedTo, setCandidateAddedTo] = useState('');

  // Today's local date string (YYYY-MM-DD) to disallow future dates
  const todayStr = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);
  const [candidateTaggedJd, setCandidateTaggedJd] = useState(null);
  const [candidateSortModel, setCandidateSortModel] = useState([]);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('bulkSchedule_pageSize');
    return saved ? Number(saved) : 50;
  });
  const [isExporting, setIsExporting] = useState(false);

  // Generate page size options including dynamic "See All"
  const pageSizeOptions = React.useMemo(() => {
    const total = totalCandidates || 0;
    const base = [12, 25, 50, 100];
    const opts = base.map(size => ({ value: size, label: `${size}` }));
    if (total > 12) {
      if (!base.includes(total)) {
        opts.push({ value: total, label: `See All (${total})` });
      } else {
        const idx = opts.findIndex(o => o.value === total);
        if (idx !== -1) {
          opts[idx] = { value: total, label: `${total} (See All)` };
        }
      }
    }
    if (pageSize && !opts.some(o => o.value === pageSize)) {
      opts.push({ value: pageSize, label: `See All (${pageSize})` });
    }
    return opts;
  }, [totalCandidates, pageSize]);

  const updatePageSize = (newSize) => {
    const safeSize = Math.max(1, Math.min(Number(newSize) || 50, 2000));
    setPageSize(safeSize);
    setPage(1);
    if ([12, 25, 50, 100].includes(safeSize)) {
      localStorage.setItem('bulkSchedule_pageSize', safeSize);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedJd) {
      toast.warning(t('bulkSchedule.selectJdFirst', 'Please select a job description first.'));
      return;
    }

    if (totalCandidates === 0 && candidates.length === 0) {
      toast.info(t('bulkSchedule.noCandidatesToExport', 'No candidates found to export.'));
      return;
    }

    if (selectedCandidateIds.length === 0) {
      toast.warning(t('bulkSchedule.selectCandidatesToExport', 'Please select at least one candidate using the checkbox to export.'));
      return;
    }

    setIsExporting(true);
    try {
      const selectedSet = new Set(selectedCandidateIds.map(id => String(id)));
      let dataToExport = [];

      // Check if all selected candidates are already present in current candidates list
      const currentSelected = candidates.filter(c => selectedSet.has(String(c.id)));
      if (currentSelected.length === selectedCandidateIds.length) {
        dataToExport = currentSelected;
      } else {
        // Fetch filtered records for this JD to find any selected candidates not in current view
        const exportParams = {
          limit: Math.min(totalCandidates || 2000, 2000),
          min_score: 0,
          page: 1,
        };
        if (candidateAddedFrom) exportParams.added_from = candidateAddedFrom;
        if (candidateAddedTo) exportParams.added_to = candidateAddedTo;
        if (candidateTaggedJd?.id) exportParams.candidate_jd_tag_id = candidateTaggedJd.id;
        const uploadSort = candidateSortModel[0];
        if (uploadSort?.field === 'uploadDate' && uploadSort.sort) {
          exportParams.ordering = uploadSort.sort === 'desc' ? '-uploaded_at' : 'uploaded_at';
        }

        const res = await getSemanticRanking(selectedJd, exportParams);
        if (res && Array.isArray(res.results)) {
          const allFetched = res.results
            .filter(r => r.is_active !== false)
            .map(r => ({
              id: r.candidate_id,
              name: r.candidate_name,
              email: r.candidate_email,
              phone: r.candidate_phone,
              uploadDate: r.resume_upload_date ? new Date(r.resume_upload_date) : null,
              interviewStatus: r.interview,
              resumeUrl: r.resume_url,
              matchScore: r.semantic_match?.final_score ?? r.final_score ?? r.match_percentage ?? 0,
              matchType: r.semantic_match?.match_type || r.match_type,
              coverage: (
                r.semantic_match?.coverage !== undefined ? r.semantic_match.coverage :
                (r.coverage !== undefined ? r.coverage : (r.breakdown?.coverage || 0))
              ) * 100,
            }));

          const candidateMap = new Map();
          allFetched.forEach(c => candidateMap.set(String(c.id), c));
          currentSelected.forEach(c => candidateMap.set(String(c.id), c));

          dataToExport = selectedCandidateIds
            .map(id => candidateMap.get(String(id)))
            .filter(Boolean);
        } else {
          dataToExport = currentSelected;
        }
      }

      if (!dataToExport || dataToExport.length === 0) {
        toast.warning(t('bulkSchedule.noCandidatesToExport', 'No candidates found to export.'));
        return;
      }

      const selectedJdObj = jdOptions.find(jd => jd.id === selectedJd);
      const jdTitle = selectedJdObj?.title || 'Job Description';

      const headers = [
        "Candidate Name",
        "Email",
        "Phone",
        "Match Score (%)",
        "Match Type",
        "Coverage (%)",
        "Upload Date",
        "Interview Status",
        "Interview Score",
        "Resume URL",
        "Job Description"
      ];

      const formatStatusForExport = (rawStatus) => {
        if (!rawStatus || rawStatus === '-') return '—';
        const s = String(rawStatus).toLowerCase();
        switch (s) {
          case 'pending': return 'Scheduled';
          case 'completed': return 'Completed';
          case 'in_progress': return 'In Progress';
          case 'expired': return 'Expired';
          case 'cancelled': return 'Cancelled';
          default: return rawStatus;
        }
      };

      const rawRows = dataToExport.map(c => {
        const uploadDateStr = c.uploadDate instanceof Date && !Number.isNaN(c.uploadDate.getTime())
          ? c.uploadDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
          : (c.uploadDate ? String(c.uploadDate) : '—');

        const ivStatus = formatStatusForExport(c.interviewStatus?.status);
        const ivScore = c.interviewStatus?.score !== null && c.interviewStatus?.score !== undefined
          ? `${Number(c.interviewStatus.score).toFixed(2)}%`
          : '—';

        return [
          c.name || '—',
          c.email || '—',
          c.phone || '—',
          c.matchScore !== null && c.matchScore !== undefined ? `${c.matchScore}%` : '—',
          c.matchType || '—',
          c.coverage !== null && c.coverage !== undefined ? `${Math.round(c.coverage)}%` : '—',
          uploadDateStr,
          ivStatus,
          ivScore,
          c.resumeUrl || '—',
          jdTitle
        ];
      });

      const sanitizedJd = jdTitle.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 30);
      const dateStr = new Date().toISOString().slice(0, 10);

      // Wide column widths: Email and Resume URL extra wide, others comfortable
      const defaultColWidths = [
        26, // Candidate Name
        36, // Email (wide)
        18, // Phone
        18, // Match Score (%)
        16, // Match Type
        16, // Coverage (%)
        18, // Upload Date
        18, // Interview Status
        18, // Interview Score
        60, // Resume URL (extra wide)
        32  // Job Description
      ];

      const dynamicColWidths = headers.map((h, i) => {
        let maxLen = h.length;
        rawRows.forEach(r => {
          const val = r[i];
          if (val) {
            const l = String(val).length;
            if (l > maxLen) maxLen = l;
          }
        });
        const baseMin = defaultColWidths[i] || 20;
        return { wch: Math.min(Math.max(maxLen + 4, baseMin), 90) };
      });

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rawRows]);
      ws['!cols'] = dynamicColWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
      XLSX.writeFile(wb, `bulk_schedule_${sanitizedJd}_${dateStr}.xlsx`);

      toast.success(t('bulkSchedule.exportSuccess', `Exported ${dataToExport.length} candidates to Excel successfully!`));
    } catch (err) {
      console.error('Failed to export candidates:', err);
      toast.error(t('bulkSchedule.exportFailed', 'Failed to export candidates. Please try again.'));
    } finally {
      setIsExporting(false);
    }
  };

  const resetState = () => {
    setSelectedJd('');
    setCandidates([]);
    setSelectedCandidateIds([]);
    setRequiredSkills([]);
    setNiceToHaveSkills([]);
    setFocusSkills([]);
    setManualSkillInput('');
    setMaxNoOfQuestions('');
    setInterviewDurationMinutes('');
    setSelectedJdDetails(null);
    setError('');
    setScheduling(false);
    setPage(1);
    setTotalPages(1);
    setTotalCandidates(0);
    setCandidateAddedFrom('');
    setCandidateAddedTo('');
    setCandidateTaggedJd(null);
    setCandidateSortModel([]);
    setGridKey(prev => prev + 1); // Force remounting grid to clear internal selection state
  };

  const [isReRunning, setIsReRunning] = useState(false);

  const handleReRunMatching = async () => {
    const currentJd = jdOptions.find(jd => jd.id === selectedJd);
    if (!currentJd || isReRunning) return;

    setIsReRunning(true);
    try {
      // Trigger matching with clear_cache flag
      await api.post(`/job-descriptions/${selectedJd}/retrigger-matching/`);

      toast.success(t('jobDescription.resumeMatchingStarted'));

      // Update local JD status to processing so polling kicks in
      setJdOptions(prev => prev.map(jd =>
        jd.id === selectedJd ? { ...jd, matching_status: 'PROCESSING' } : jd
      ));

      // Clear current candidates while we wait
      setCandidates([]);
      setTotalCandidates(0);

    } catch (err) {
      console.error('Failed to re-run matching:', err);
      toast.error(t('jobDescription.failedToStartMatching'));
    } finally {
      setIsReRunning(false);
    }
  };

  const handleCancel = () => {
    resetState();
    toast.info(t('bulkSchedule.toast.cancelled'));
  };

  const [refreshJdListTrigger, setRefreshJdListTrigger] = useState(0);

  // Fetch Job Descriptions
  useEffect(() => {
    const fetchJds = async (query = '') => {
      setLoadingJds(true);
      setError('');
      try {
        const params = new URLSearchParams({ page_size: '100', search: query });
        const res = await api.get(`job-descriptions/list_with_pagination/?${params.toString()}`);
        if (res.data && res.data.results) {
          setJdOptions(res.data.results);
        } else {
          setJdOptions([]);
        }
      } catch (err) {
        console.error('Failed to fetch job descriptions', err);
        setError(t('bulkSchedule.toast.loadJdError'));
        setJdOptions([]);
      } finally {
        setLoadingJds(false);
      }
    };

    fetchJds(debouncedSearchTerm);
  }, [debouncedSearchTerm, t, refreshJdListTrigger]);

  const selectNewJd = async (newJdId) => {
    setRefreshJdListTrigger(prev => prev + 1);
    setTimeout(() => {
      setSelectedJd(newJdId);
      setPage(1);
    }, 500); // Wait for refetch to complete
  };

  const handleJobDescriptionSubmit = async (e) => {
    e.preventDefault();
    setJobDescriptionError("");
    const { jdPayload, benchmarks, approve } = splitJdFormAndBenchmarks(jobDescriptionForm);
    try {
      const res = await api.post("job-descriptions/", jdPayload);
      if (res.data?.id && benchmarks.length) {
        try {
          await saveJdBenchmarks(api, res.data.id, benchmarks, { approve });
        } catch (benchErr) {
          toast.error(t('jobDescription.scorecardSaveFailed', 'Job saved, but the interview benchmarks could not be saved. Open the job to finish it.'));
        }
      }
      toast.success(t('jobDescription.jobDescriptionAdded', 'Job Description successfully added!'));
      setShowJobDescriptionModal(false);
      setJobDescriptionForm(initialFormState);

      if (res.data && res.data.id) {
        selectNewJd(res.data.id);
      } else {
        setRefreshJdListTrigger(prev => prev + 1);
      }
    } catch (err) {
      toast.error(t('jobDescription.failedToAddJD', 'Failed to add Job Description.'));
    }
  };

  const handleUploadJD = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('jobDescription.invalidFileType', 'Invalid file type. Only PDF and DOCX are allowed.'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('jobDescription.fileSizeExceeded', 'File size exceeds 10MB limit.'));
      return;
    }

    setUploadingJD(true);
    handleDropdownClose();
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('job-descriptions/parse-from-file/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        let jobDescriptionText = response.data.description || "";
        if (response.data.title) {
          const titleRegex = new RegExp(`^#{1,3}\\s*${response.data.title.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}\\s*`, "gm");
          jobDescriptionText = jobDescriptionText.replace(titleRegex, "");
        }
        const htmlContent = convertMarkdownToHtml(jobDescriptionText);

        setJobDescriptionForm({
          ...initialFormState,
          title: response.data.title || "",
          description: htmlContent,
          employment_type: response.data.employment_type || "",
          experience_level: response.data.experience_level || "",
          required_skills: response.data.required_skills || [],
          nice_to_have_skills: response.data.nice_to_have_skills || [],
          salary_range_min: response.data.salary_range_min || "",
          salary_range_max: response.data.salary_range_max || "",
          work_type: response.data.work_type || "",
        });

        setShowJobDescriptionModal(true);
        toast.success(t('jobDescription.jdExtractedSuccess', 'Job Description text extracted successfully!'));
      } else {
        throw new Error('Failed to extract job description');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t('jobDescription.failedToParseJD', 'Failed to parse Job Description.'));
    } finally {
      setUploadingJD(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAudioJDApply = async (data) => {
    if (!data) return;

    setJobDescriptionForm({
      ...initialFormState,
      title: data.title || "",
      description: data.description || "",
      employment_type: data.employment_type || "",
      experience_level: data.experience_level || "",
      required_skills: data.required_skills || data.required_skills_list || [],
      nice_to_have_skills: data.nice_to_have_skills || data.nice_to_have_skills_list || [],
      questions: data.questions || data.interview_questions || [],
      work_type: data.work_type || "on_site",
      salary_range_min: data.salary_range_min || "",
      salary_range_max: data.salary_range_max || "",
    });

    setShowAudioModal(false);
    setShowJobDescriptionModal(true);
  };

  const handleAddDepartment = async (e) => {
    e.preventDefault();
    try {
      const companyId = departments.length > 0 ? departments[0].company : 1;
      const res = await api.post("departments/", { name: newDepartmentName, company: companyId });
      setDepartments(prev => [...(Array.isArray(prev) ? prev : []), res.data]);
      toast.success(t('jobDescription.departmentAdded', 'Department successfully added!'));
      setShowAddDepartmentModal(false);
      setNewDepartmentName("");
    } catch (err) {
      toast.error(t('jobDescription.failedToAddDepartment', 'Failed to add department.'));
    }
  };

  const handleJdChange = (event, value) => {
    // value will be the selected JD object or null
    setSelectedJd(value ? value.id : '');
    setPage(1); // Reset page to 1 on JD change
  };
  const handleJdInputChange = (event, newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
      setSearchTerm(newInputValue);
    }
  };
  const fetchCandidates = useCallback(async (pageToFetch = 1, jdToFetch = selectedJd) => {
    if (!jdToFetch) return;

    setLoadingCandidates(true);
    setError('');
    try {
      const rankingParams = {
        limit: pageSize,
        min_score: 0,
        page: pageToFetch,
      };
      if (candidateAddedFrom) {
        rankingParams.added_from = candidateAddedFrom;
      }
      if (candidateAddedTo) {
        rankingParams.added_to = candidateAddedTo;
      }
      if (candidateTaggedJd?.id) {
        rankingParams.candidate_jd_tag_id = candidateTaggedJd.id;
      }
      const uploadSort = candidateSortModel[0];
      if (uploadSort?.field === 'uploadDate' && uploadSort.sort) {
        rankingParams.ordering = uploadSort.sort === 'desc' ? '-uploaded_at' : 'uploaded_at';
      }

      const data = await getSemanticRanking(jdToFetch, rankingParams);

      if (data && Array.isArray(data.results)) {
        const formattedCandidates = data.results
          .filter(result => result.is_active !== false)
          .map(result => ({
            id: result.candidate_id,
            name: result.candidate_name,
            email: result.candidate_email,
            phone: result.candidate_phone,
            uploadDate: result.resume_upload_date ? new Date(result.resume_upload_date) : null,
            interviewStatus: result.interview,
            resumeUrl: result.resume_url,
            matchScore: result.semantic_match?.final_score ?? result.final_score ?? result.match_percentage ?? 0,
            matchType: result.semantic_match?.match_type || result.match_type,
            coverage: (
              result.semantic_match?.coverage !== undefined ? result.semantic_match.coverage :
              (result.coverage !== undefined ? result.coverage : (result.breakdown?.coverage || 0))
            ) * 100,
            breakdown: result.semantic_match?.breakdown || result.breakdown,
            experience: result.semantic_match ? {
              candidate: result.semantic_match.candidate_experience_years,
              required: result.semantic_match.required_experience_years
            } : null,
          }));
        setCandidates(formattedCandidates);
        
        if (data.pagination) {
          setTotalCandidates(data.pagination.total_count);
          setTotalPages(data.pagination.total_pages);
          setPage((prev) => (Number(prev) === Number(data.pagination.current_page) ? prev : data.pagination.current_page));
        } else {
          setTotalCandidates(data.results_count || formattedCandidates.length);
          setTotalPages(1);
        }
      } else {
        setCandidates([]);
        setTotalCandidates(0);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Failed to fetch candidates', err);
      setError(t('bulkSchedule.toast.loadCandidatesError'));
      setCandidates([]);
      setTotalCandidates(0);
      setTotalPages(1);
    } finally {
      setLoadingCandidates(false);
    }
  }, [selectedJd, t, candidateAddedFrom, candidateAddedTo, candidateTaggedJd?.id, candidateSortModel, pageSize]);

  useEffect(() => {
    setPage(1);
    setSelectedCandidateIds([]);
    setGridKey(prev => prev + 1);
  }, [candidateAddedFrom, candidateAddedTo, candidateTaggedJd?.id, selectedJd]);

  // Fetch Candidates when selectedJd, page, or pageSize changes
  useEffect(() => {
    if (selectedJd) {
      fetchCandidates(page, selectedJd);
    } else {
      setCandidates([]);
      setSelectedCandidateIds([]);
      setTotalCandidates(0);
      setTotalPages(1);
      setGridKey(prev => prev + 1);
    }
  }, [selectedJd, page, pageSize, fetchCandidates]);

  // Fetch Skills and full configuration from selected JD
  useEffect(() => {
    let isMounted = true;
    if (selectedJd) {
      const fetchSkills = async () => {
        try {
          // Task B2: GET /api/jd/{id}/skills
          const res = await api.get(`/jd/${selectedJd}/skills`);
          if (isMounted && res.data) {
            setRequiredSkills(res.data.required_skills || []);
            setNiceToHaveSkills(res.data.nice_to_have_skills || []);
            setFocusSkills([]); // Start with no focus skills pre-selected
          }
        } catch (err) {
          console.error('Failed to fetch JD skills', err);
          if (isMounted) {
            setError(t('bulkSchedule.toast.loadSkillsError'));
            setRequiredSkills([]);
            setNiceToHaveSkills([]);
            setFocusSkills([]);
          }
        }
      };

      const fetchJdDetails = async () => {
        try {
          const res = await api.get(`job-descriptions/${selectedJd}/`);
          if (isMounted && res.data) {
            setSelectedJdDetails(res.data);

            const companyId = res.data.department?.company || res.data.company_id;
            if (companyId) {
              try {
                const compRes = await api.get(`/companies/${companyId}/`);
                if (isMounted && compRes.data?.settings?.interview_settings) {
                  setCompanyInterviewSettings(compRes.data.settings.interview_settings);
                }
              } catch (e) {
                console.warn('Could not load company settings by JD company ID:', e);
              }
            }
          }
        } catch (err) {
          console.warn('Could not load full JD details:', err);
        }
      };

      fetchSkills();
      fetchJdDetails();
    } else {
      setRequiredSkills([]);
      setNiceToHaveSkills([]);
      setFocusSkills([]);
      setSelectedJdDetails(null);
    }
    return () => { isMounted = false; };
  }, [selectedJd, t]);

  // Load initial company interview settings from auth token if available
  useEffect(() => {
    let isMounted = true;
    const loadCompanySettingsFromToken = async () => {
      try {
        const token = localStorage.getItem('token');
        let companyId = null;
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            companyId = payload.company_id || payload.company;
          } catch (e) {}
        }
        if (companyId) {
          const compRes = await api.get(`/companies/${companyId}/`);
          if (isMounted && compRes.data?.settings?.interview_settings) {
            setCompanyInterviewSettings(compRes.data.settings.interview_settings);
          }
        }
      } catch (err) {
        console.warn('Could not load company settings from token:', err);
      }
    };
    loadCompanySettingsFromToken();
    return () => { isMounted = false; };
  }, []);

  const selectedMatchingStatus = jdOptions.find(jd => String(jd.id) === String(selectedJd))?.matching_status;

  // Poll for matching status updates
  useEffect(() => {
    if (!selectedJd || selectedMatchingStatus?.toLowerCase() !== 'processing') {
      return undefined;
    }

    const interval = setInterval(async () => {
      try {
        const res = await api.get(`job-descriptions/${selectedJd}/`);
        const nextStatus = res.data?.matching_status;

        setJdOptions(prev => prev.map(jd => {
          if (String(jd.id) !== String(selectedJd)) return jd;
          if (jd.matching_status === nextStatus) return jd;
          return { ...jd, matching_status: nextStatus };
        }));

        if (nextStatus?.toLowerCase() === 'ready' || nextStatus?.toLowerCase() === 'completed') {
          fetchCandidates(page, selectedJd);
        }
      } catch (err) {
        console.error('Error polling JD status:', err);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedJd, selectedMatchingStatus, page, fetchCandidates]);

  const handleAddFocusSkill = (skill) => {
    if (skill && !focusSkills.includes(skill)) {
      setFocusSkills((prevSkills) => [...prevSkills, skill]);
    }
  };

  const handleAddManualSkill = () => {
    const skill = manualSkillInput.trim();
    if (skill && !focusSkills.includes(skill)) {
      setFocusSkills((prevSkills) => [...prevSkills, skill]);
      setManualSkillInput('');
    }
  };

  const handleDeleteSkill = (skillToDelete) => {
    setFocusSkills((prevSkills) => prevSkills.filter((skill) => skill !== skillToDelete));
  };

  useEffect(() => {
    let interval;
    if (taskId) {
      const checkProgress = async () => {
        try {
          const res = await api.get(`/interviews/bulk-schedule-progress/${taskId}/`);
          setProgressData(res.data);
          if (res.data.status === 'completed' || res.data.progress === 100) {
            clearInterval(interval);
            setTaskId(null); // Stop polling
          }
        } catch (error) {
          console.error('Failed to fetch progress', error);
          clearInterval(interval);
          setTaskId(null); // Stop polling on error
          toast.error(t('bulkSchedule.toast.progressError'));
        }
      };

      checkProgress();
      interval = setInterval(checkProgress, 1000);
    }
    return () => clearInterval(interval); // Cleanup on unmount or when taskId changes
  }, [taskId, t]);

  const handleScheduleInterviews = async () => {
    if (!selectedJd) {
      toast.error(t('bulkSchedule.toast.selectJdError'));
      return;
    }
    if (selectedCandidateIds.length === 0) {
      toast.error(t('bulkSchedule.toast.selectCandidateError'));
      return;
    }

    const trimmedQuestions = String(maxNoOfQuestions ?? '').trim();
    if (trimmedQuestions !== '' && Number(trimmedQuestions) < 5) {
      toast.error(t('bulkSchedule.questionsMinError', 'Minimum number of questions must be at least 5'));
      return;
    }

    const trimmedDuration = String(interviewDurationMinutes ?? '').trim();
    if (trimmedDuration !== '' && (Number(trimmedDuration) < 15 || Number(trimmedDuration) > 120)) {
      toast.error(t('bulkSchedule.durationRangeError', 'Duration must be between 15 and 120 minutes'));
      return;
    }

    setScheduling(true);
    setError('');
    toast.info(t('bulkSchedule.toast.initiating'));
    try {
      // Task B3: POST /api/interviews/bulk-schedule
      const payload = {
        jd_id: selectedJd,
        candidate_ids: selectedCandidateIds,
        focus_skills: focusSkills,
        max_no_of_questions: trimmedQuestions !== '' ? Number(trimmedQuestions) : (defaultQuestions != null ? Number(defaultQuestions) : null),
        interview_duration_minutes: trimmedDuration !== '' ? Number(trimmedDuration) : (defaultDuration || 30),
      };
      const res = await api.post('/interviews/bulk-schedule/', payload);
      if (res.data && res.data.task_id) {
        setProgressData({
          progress: 0,
          completed_count: 0,
          total_tasks: selectedCandidateIds.length,
          status: 'in_progress'
        });
        setTaskId(res.data.task_id);
        setIsProgressModalOpen(true);
      } else {
        toast.error(t('bulkSchedule.toast.startError'));
      }
    } catch (err) {
      console.error('Failed to schedule interviews', err);
      if (err.response?.status === 503) {
        toast.error(t('bulkSchedule.toast.serviceUnavailable'));
      } else {
        toast.error(t('bulkSchedule.toast.genericError'));
      }
    } finally {
      setScheduling(false);
    }
  };

  const getStatusConfig = useCallback((status) => {
    switch (String(status || '').toLowerCase()) {
      case 'completed':
        return { icon: CheckCircle, bgColor: 'bg-emerald-50', textColor: 'text-emerald-700', borderColor: 'border-emerald-200', iconColor: 'text-emerald-500', label: t('interviewOverview.completed', 'Completed') };
      case 'in_progress':
        return { icon: Clock, bgColor: 'bg-sky-50', textColor: 'text-sky-700', borderColor: 'border-sky-200', iconColor: 'text-sky-500', label: t('interviewOverview.inProgress', 'In Progress') };
      case 'pending':
        return { icon: Calendar, bgColor: 'bg-amber-50', textColor: 'text-amber-700', borderColor: 'border-amber-200', iconColor: 'text-amber-500', label: t('interviewOverview.scheduled', 'Scheduled') };
      case 'expired':
        return { icon: XCircle, bgColor: 'bg-rose-50', textColor: 'text-rose-700', borderColor: 'border-rose-200', iconColor: 'text-rose-500', label: t('interviewOverview.expired', 'Expired') };
      case 'cancelled':
        return { icon: AlertCircle, bgColor: 'bg-gray-50', textColor: 'text-gray-600', borderColor: 'border-gray-200', iconColor: 'text-gray-400', label: t('interviewOverview.cancelled', 'Cancelled') };
      default:
        return { icon: AlertCircle, bgColor: 'bg-gray-50', textColor: 'text-gray-600', borderColor: 'border-gray-200', iconColor: 'text-gray-400', label: status || t('interviewOverview.unknown', 'Unknown') };
    }
  }, [t]);

  const candidateColumns = React.useMemo(() => [
    {
      field: 'name',
      headerName: t('bulkSchedule.candidateName'),
      flex: 1.2,
      minWidth: 140,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            ...FONT,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '0.8125rem',
            color: '#0f172a',
            '&:hover': { color: '#0ea5e9' },
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          onClick={() => navigate(`/candidates/${params.row.id}`)}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'email',
      headerName: t('bulkSchedule.candidateEmail'),
      flex: 1.4,
      minWidth: 160,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: '0.75rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.value}
        </Box>
      )
    },
    {
      field: 'phone',
      headerName: t('bulkSchedule.candidatePhone', 'Phone'),
      flex: 0.8,
      minWidth: 100,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', fontSize: '0.75rem', color: '#64748b' }}>
          {params.value || '—'}
        </Box>
      )
    },
    {
      field: 'matchScore',
      headerName: t('bulkSchedule.matchScore'),
      flex: 0.8,
      minWidth: 110,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const score = params.value;
        const currentJd = jdOptions.find(jd => jd.id === selectedJd);
        const isProcessing = currentJd?.matching_status?.toLowerCase() === 'processing';
        const matchType = params.row.matchType;

        if (isProcessing) {
          return (
            <span className="px-2 py-0.5 text-[11px] font-medium rounded-full border bg-sky-50 text-sky-700 border-sky-200 animate-pulse" style={FONT}>
              {t('bulkSchedule.calculating')}
            </span>
          );
        }

        if (score === null || score === undefined) {
          return (
            <span className="px-2 py-0.5 text-[11px] font-medium rounded-full border bg-slate-100 text-slate-500 border-slate-200" style={FONT}>
              {t('bulkSchedule.pending', 'Pending')}
            </span>
          );
        }

        let colorClass = "bg-red-50 text-red-700 border-red-200";
        if (score >= 70) {
          colorClass = "bg-green-50 text-green-700 border-green-200";
        } else if (score >= 40) {
          colorClass = "bg-amber-50 text-amber-700 border-amber-200";
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
            <Tooltip
              title={
                <Box sx={{ p: 1.5, maxWidth: 280, bgcolor: 'white' }}>
                  <Typography variant="subtitle2" sx={{ ...FONT, fontWeight: 600, mb: 1, borderBottom: '1px solid #e2e8f0', pb: 0.5, fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 1, color: '#1e293b' }}>
                    <Award size={14} className="text-yellow-600" /> {t('bulkSchedule.matchBreakdown')}
                  </Typography>
                  
                  {params.row.breakdown ? (
                    <Box sx={{ spaceY: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>Coverage</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>{params.row.coverage}%</Typography>
                      </Box>
                      <Box sx={{ width: '100%', h: 4, bgcolor: 'grey.100', borderRadius: 1, mb: 1, overflow: 'hidden' }}>
                        <Box sx={{ width: `${params.row.coverage}%`, h: '100%', bgcolor: 'primary.main' }} />
                      </Box>

                      {params.row.breakdown.experience_factor !== 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'success.light', px: 1, py: 0.5, borderRadius: 1, mb: 1, opacity: 0.8 }}>
                          <Typography variant="caption" sx={{ color: 'success.dark', fontSize: '0.65rem' }}>Experience Bonus</Typography>
                          <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 'bold', fontSize: '0.65rem' }}>+{params.row.breakdown.experience_factor}%</Typography>
                        </Box>
                      )}

                      {params.row.experience && (
                        <Box sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>Experience Match</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem' }}>{params.row.experience.candidate}y / {params.row.experience.required}y</Typography>
                          </Box>
                          <Box sx={{ width: '100%', h: 2, bgcolor: 'grey.100', borderRadius: 0.5, overflow: 'hidden' }}>
                            <Box sx={{ 
                              width: `${Math.min(100, (params.row.experience.candidate / (params.row.experience.required || 1)) * 100)}%`, 
                              h: '100%', 
                              bgcolor: 'primary.light' 
                            }} />
                          </Box>
                        </Box>
                      )}

                      <Typography variant="caption" sx={{ display: 'block', mt: 1, mb: 0.5, fontWeight: 'bold', fontSize: '0.65rem', color: 'text.secondary', textTransform: 'uppercase' }}>Skill Similarities</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 100, overflowY: 'auto' }}>
                        {[
                          ...(params.row.breakdown.required_skills || []),
                          ...(params.row.breakdown.optional_skills || [])
                        ].filter(s => s.status === 'matched').map((detail, i) => (
                          <Box key={i} sx={{ px: 1, py: 0.25, bgcolor: 'grey.50', border: '1px solid #e2e8f0', borderRadius: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, minWidth: '100%' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold', color: '#1e293b' }}>{detail.jd_skill}</Typography>
                              {detail.best_match && detail.best_match.toLowerCase() !== detail.jd_skill.toLowerCase() && (
                                <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#64748b' }}>matched as: {detail.best_match}</Typography>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold', color: 'success.main' }}>{(detail.similarity * 100).toFixed(0)}%</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: '#64748b' }}>Detailed breakdown unavailable.</Typography>
                  )}
                </Box>
              }
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'white',
                    color: 'rgba(0, 0, 0, 0.87)',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                    border: '1px solid #e2e8f0',
                    '& .MuiTooltip-arrow': {
                      color: 'white',
                      '&::before': {
                        border: '1px solid #e2e8f0',
                        backgroundColor: 'white',
                      },
                    },
                  },
                },
              }}
              arrow
              placement="top"
              enterTouchDelay={0}
            >
              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border cursor-pointer ${colorClass} flex items-center gap-1`} style={FONT}>
                {matchType === 'semantic' && <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'currentColor' }} />}
                {score}%
              </span>
            </Tooltip>
            <Box
              sx={{
                width: 28,
                height: 4,
                bgcolor: 'grey.200',
                borderRadius: 1,
                overflow: 'hidden',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              <Box
                sx={{
                  width: `${score}%`,
                  height: '100%',
                  bgcolor: score >= 70 ? 'success.main' : score >= 40 ? 'warning.main' : 'error.main',
                }}
              />
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'interviewStatus',
      headerName: t('bulkSchedule.interviewStatus'),
      flex: 0.9,
      minWidth: 115,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const rawStatus = params.value?.status;
        const score = params.value?.score;

        if (!rawStatus || rawStatus === '-') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <span className="text-gray-400 font-medium text-xs">—</span>
            </Box>
          );
        }

        const config = getStatusConfig(rawStatus);
        const StatusIcon = config.icon;
        const isCompleted = String(rawStatus).toLowerCase() === 'completed';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${config.bgColor} ${config.textColor} border ${config.borderColor} text-[10px] font-medium leading-none`}
              style={FONT}
            >
              <StatusIcon size={10} className={config.iconColor} />
              <span className="truncate">{config.label}</span>
            </span>
            {isCompleted && score !== null && score !== undefined && (
              <span style={{ ...FONT, fontWeight: 600, color: '#16a34a', fontSize: '0.7rem' }}>
                ({typeof score === 'number' ? `${score.toFixed(1)}%` : score})
              </span>
            )}
          </Box>
        );
      },
    },
    {
      field: 'resumeUrl',
      headerName: t('bulkSchedule.resume'),
      flex: 0.6,
      minWidth: 90,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value ? (
            <a
              href={params.value}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="flex items-center gap-1 text-sky-600 hover:text-sky-700 text-xs font-medium"
            >
              {t('bulkSchedule.viewResume')} <ExternalLink size={12} />
            </a>
          ) : (
            <span className="text-xs text-gray-400">{t('bulkSchedule.nA')}</span>
          )}
        </Box>
      ),
    },
  ], [theme, t, jdOptions, selectedJd, getStatusConfig]);

  const isScheduleButtonDisabled = !selectedJd || selectedCandidateIds.length === 0 || scheduling || jdOptions.find(jd => jd.id === selectedJd)?.matching_status?.toLowerCase() === 'processing';

  const handleCellClick = (params) => {
    // Navigate to candidate detail if clicking name column
    if (params.field === 'name') {
      navigate(`/candidates/${params.row.id}`);
    }
  };

  const handleCloseProgressModal = () => {
    setIsProgressModalOpen(false);
    setProgressData(null);
    setTaskId(null);
    if (selectedJd) {
      fetchCandidates(page, selectedJd);
    }
  };

  const isQuestionsError = maxNoOfQuestions !== '' && Number(maxNoOfQuestions) < 5;
  const isDurationError = interviewDurationMinutes !== '' && (Number(interviewDurationMinutes) < 15 || Number(interviewDurationMinutes) > 120);

  return (
    <PageShell className="bg-gray-50/50" style={FONT}>
      <BulkScheduleProgressModal
        open={isProgressModalOpen}
        onClose={handleCloseProgressModal}
        progressData={progressData}
        expectedTotal={selectedCandidateIds.length}
      />
      <PageCard overflow={false} className="overflow-hidden">
        <PageHeader
          title={t('nav.bulkScheduleHeading', 'Bulk Schedule')}
          subtitle={t('nav.bulkScheduleSubheading', 'Manage and schedule multiple interview sessions')}
        >
              <PageHeaderButton
                id="create-bulk-jd-menu-button"
                aria-controls={dropdownOpen ? 'create-bulk-jd-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={dropdownOpen ? 'true' : undefined}
                onClick={handleDropdownClick}
                title={t('jobDescription.addJobDescription', 'Add Job Description')}
                icon={uploadingJD ? <CircularProgress size={15} color="inherit" /> : <Plus size={16} />}
              >
                {t('jobDescription.addJobDescription', 'Add Job Description')}
                <ChevronDown
                  size={15}
                  className={`transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                />
              </PageHeaderButton>
              <Menu
                anchorEl={anchorEl}
                open={dropdownOpen}
                onClose={handleDropdownClose}
                MenuListProps={{ dense: true, disablePadding: true }}
                PaperProps={{
                  elevation: 4,
                  sx: {
                    mt: 0.5,
                    borderRadius: '8px',
                    width: anchorEl ? anchorEl.offsetWidth : undefined,
                    minWidth: anchorEl ? anchorEl.offsetWidth : undefined,
                    maxWidth: anchorEl ? anchorEl.offsetWidth : undefined,
                    overflow: 'hidden',
                    border: '1px solid #e2e8f0',
                    '& .MuiMenuItem-root': {
                      px: 1.5,
                      py: 0.75,
                      minHeight: 34,
                      mx: 0,
                      my: 0,
                      borderRadius: 0,
                      gap: 1,
                      fontSize: '0.8125rem',
                      lineHeight: 1.3,
                      '&:not(:last-of-type)': {
                        borderBottom: '1px solid #f1f5f9',
                      },
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => { handleDropdownClose(); setJobDescriptionForm(initialFormState); setShowJobDescriptionModal(true); }}>
                  <ListItemIcon sx={{ color: '#3b82f6', minWidth: '22px !important' }}>
                    <Plus size={14} />
                  </ListItemIcon>
                  <ListItemText primary={t('jobDescription.addManually', 'Add Manually')} primaryTypographyProps={{ fontSize: '0.8125rem', noWrap: true }} />
                </MenuItem>
                <input type="file" ref={fileInputRef} accept=".pdf,.doc,.docx" onChange={handleUploadJD} className="hidden" id="jd-bulk-file-upload" />
                <MenuItem component="label" htmlFor="jd-bulk-file-upload">
                  <ListItemIcon sx={{ color: '#22c55e', minWidth: '22px !important' }}>
                    {uploadingJD ? <CircularProgress size={14} color="inherit" /> : <Upload size={14} />}
                  </ListItemIcon>
                  <ListItemText primary={t('jobDescription.uploadFromFile', 'Upload from File')} primaryTypographyProps={{ fontSize: '0.8125rem', noWrap: true }} />
                </MenuItem>
                <MenuItem onClick={() => { handleDropdownClose(); setShowAudioModal(true); }}>
                  <ListItemIcon sx={{ color: '#a855f7', minWidth: '22px !important' }}>
                    <Mic size={14} />
                  </ListItemIcon>
                  <ListItemText primary={t('jobDescription.generateFromAudio', 'Generate from Audio')} primaryTypographyProps={{ fontSize: '0.8125rem', noWrap: true }} />
                </MenuItem>
              </Menu>
        </PageHeader>
      <div className="flex flex-1 min-h-0 flex-col px-3 py-3 sm:px-4">
      <div className="flex gap-2 mb-3 shrink-0">
        <button
          type="button"
          onClick={() => setActiveTab('schedule')}
          style={{
            fontFamily: FONT.fontFamily,
            ...(activeTab === 'schedule' ? { background: GRADIENT } : {}),
          }}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer ${
            activeTab === 'schedule'
              ? 'text-white shadow-sm'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
        >
          {t('bulkSchedule.scheduleTab', 'Schedule')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('followUp')}
          style={{
            fontFamily: FONT.fontFamily,
            ...(activeTab === 'followUp' ? { background: GRADIENT } : {}),
          }}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 cursor-pointer ${
            activeTab === 'followUp'
              ? 'text-white shadow-sm'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
        >
          {t('bulkSchedule.followUpTab', 'Reminders')}
        </button>
      </div>
      {activeTab === 'followUp' && (
        <div className="w-full flex-1 min-h-0 overflow-y-auto">
          <BulkScheduleFollowUp
            jdOptions={jdOptions}
            selectedJd={selectedJd}
            onJdChange={handleJdChange}
            onJdInputChange={handleJdInputChange}
            searchTerm={searchTerm}
            loadingJds={loadingJds}
          />
        </div>
      )}
      {activeTab === 'schedule' && (
        <div className="w-full flex-1 min-h-0 flex flex-col overflow-hidden" style={FONT}>
          <Step
            number={1}
            title={t('bulkSchedule.selectJdStep')}
            action={
              selectedJd ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setShowFocusAreaDrawer(true)}
                  startIcon={<Sliders size={14} />}
                  sx={{
                    ...buttonSx,
                    background: GRADIENT,
                    color: '#fff',
                    px: 2,
                    py: 0.6,
                    fontSize: '0.75rem',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px -1px rgba(14, 165, 233, 0.35)',
                    '&:hover': {
                      background: GRADIENT,
                      opacity: 0.95,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <span>{t('bulkSchedule.defineFocusAreaBtn', 'Define Focus Area')}</span>
                  {focusSkills.length > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.2 text-[10px] font-bold bg-white/25 text-white rounded-full">
                      {focusSkills.length}
                    </span>
                  )}
                </Button>
              ) : null
            }
          />
          <Paper sx={paperSx}>
            <BulkScheduleJdSelect
              jdOptions={jdOptions}
              selectedJd={selectedJd}
              onChange={handleJdChange}
              onInputChange={handleJdInputChange}
              searchTerm={searchTerm}
              loadingJds={loadingJds}
              sx={{ mb: selectedJd ? 1.5 : 0 }}
            />
            {selectedJd && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ ...FONT, fontSize: '0.75rem', color: '#64748b' }}>
                  {t('jobDescription.matchingStatus')}:
                </Typography>
                <Box sx={{
                  display: 'inline-flex',
                  px: 1.25,
                  py: 0.35,
                  borderRadius: '8px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  ...FONT,
                  bgcolor: (jdOptions.find(jd => jd.id === selectedJd)?.matching_status?.toLowerCase() === 'processing') ? '#eff6ff' : '#f0fdf4',
                  color: (jdOptions.find(jd => jd.id === selectedJd)?.matching_status?.toLowerCase() === 'processing') ? '#0369a1' : '#15803d',
                  border: '1px solid',
                  borderColor: (jdOptions.find(jd => jd.id === selectedJd)?.matching_status?.toLowerCase() === 'processing') ? '#bae6fd' : '#bbf7d0',
                  animation: (jdOptions.find(jd => jd.id === selectedJd)?.matching_status?.toLowerCase() === 'processing') ? 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' : 'none',
                }}>
                  {(() => {
                    const status = jdOptions.find(jd => jd.id === selectedJd)?.matching_status;
                    return status ? t(`jobDescription.status${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`, status) : t('jobDescription.statusNotStarted', 'Not Started');
                  })()}
                </Box>
                {jdOptions.find(jd => jd.id === selectedJd)?.matching_status?.toLowerCase() === 'processing' && (
                  <Typography variant="caption" sx={{ ...FONT, color: '#0284c7', ml: 1, fontSize: '0.7rem' }}>
                    {t('jobDescription.matchingProcessingHint', 'Matching is in progress. Scores will appear once completed.')}
                  </Typography>
                )}

                <Button
                  size="small"
                  startIcon={isReRunning ? <CircularProgress size={12} color="inherit" /> : <RefreshCw size={12} />}
                  onClick={handleReRunMatching}
                  disabled={isReRunning || jdOptions.find(jd => jd.id === selectedJd)?.matching_status?.toLowerCase() === 'processing'}
                  sx={{
                    ...buttonSx,
                    ml: 'auto',
                    py: 0.5,
                    px: 1.5,
                    fontSize: '0.72rem',
                    borderRadius: '6px',
                    color: '#64748b',
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    '&:hover': {
                      bgcolor: '#f1f5f9',
                      borderColor: '#cbd5e1',
                      color: '#0f172a',
                    },
                    '&:disabled': {
                      bgcolor: '#f8fafc',
                      opacity: 0.6,
                    },
                  }}
                >
                  {isReRunning ? t('bulkSchedule.rerunning') : t('jobDescription.clearCachesButton', 'Clear Cache & Re-run')}
                </Button>
              </Box>
            )}

            {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: '8px', ...FONT, fontSize: '0.75rem', py: 0.5 }}>{error}</Alert>}
          </Paper>

          {selectedJd && (
            <>
              {jdOptions.find(jd => jd.id === selectedJd)?.matching_status?.toLowerCase() === 'processing' ? (
                <Paper sx={{ ...paperSx, p: 4, textAlign: 'center', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                    <AptiviewLoader size="sm" />
                  </Box>
                  <Typography sx={{ ...FONT, fontWeight: 600, fontSize: '0.95rem', color: '#475569', mb: 0.5 }}>
                    {t('jobDescription.statusProcessing')}...
                  </Typography>
                  <Typography sx={{ ...FONT, fontSize: '0.8125rem', color: '#64748b' }}>
                    {t('jobDescription.matchingProcessingHint')}
                  </Typography>
                </Paper>
              ) : (
                <>
                  <Step number={2} title={t('bulkSchedule.selectCandidatesStep')} />

                  {/* Filter candidates bar */}
                  <div className="bg-white rounded-xl p-2.5 sm:p-3 border border-gray-200/80 shadow-2xs mb-2 sm:mb-2.5 shrink-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mr-1 shrink-0">
                        <Filter size={14} className="text-blue-600" />
                        <span>{t('bulkSchedule.filterCandidates', 'Filter Candidates')}:</span>
                      </div>

                      {/* Added from Date Picker */}
                      <PremiumDatePicker
                        compact
                        prefix={t('bulkSchedule.addedFrom', 'From') + ':'}
                        value={candidateAddedFrom}
                        disableFuture
                        max={candidateAddedTo && candidateAddedTo < todayStr ? candidateAddedTo : todayStr}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) {
                            setCandidateAddedFrom('');
                            return;
                          }
                          if (val > todayStr) {
                            setCandidateAddedFrom(todayStr);
                          } else if (candidateAddedTo && val > candidateAddedTo) {
                            setCandidateAddedFrom(candidateAddedTo);
                          } else {
                            setCandidateAddedFrom(val);
                          }
                        }}
                      />

                      {/* Added to Date Picker */}
                      <PremiumDatePicker
                        compact
                        prefix={t('bulkSchedule.addedTo', 'To') + ':'}
                        value={candidateAddedTo}
                        disableFuture
                        min={candidateAddedFrom || undefined}
                        max={todayStr}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) {
                            setCandidateAddedTo('');
                            return;
                          }
                          if (val > todayStr) {
                            setCandidateAddedTo(todayStr);
                          } else if (candidateAddedFrom && val < candidateAddedFrom) {
                            setCandidateAddedTo(candidateAddedFrom);
                          } else {
                            setCandidateAddedTo(val);
                          }
                        }}
                      />

                      {/* Tagged with JD Autocomplete */}
                      <div className="min-w-[200px] max-w-sm flex-1">
                        <Autocomplete
                          size="small"
                          options={jdOptions}
                          value={candidateTaggedJd}
                          onChange={(event, value) => setCandidateTaggedJd(value)}
                          getOptionLabel={(option) => option?.title || ''}
                          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                          ListboxProps={{ sx: jdAutocompleteListboxSx }}
                          PaperProps={jdAutocompletePaperProps}
                          slotProps={{ popper: { sx: { zIndex: 1400, minWidth: 320 } } }}
                          renderOption={(props, option) => {
                            const { key, ...rest } = props;
                            return (
                              <li key={option.id || key} {...rest}>
                                <BulkScheduleJdOption option={option} compact showStatus={false} />
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              placeholder={t('bulkSchedule.taggedWithJdPlaceholder', 'Tagged with JD (optional)')}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <Briefcase size={13} className="text-gray-400 mr-1.5 shrink-0" />
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                                sx: {
                                  height: 36,
                                  borderRadius: '8px',
                                  fontSize: '0.8125rem',
                                  fontFamily: FONT.fontFamily,
                                  backgroundColor: '#fff',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d1d5db',
                                    borderWidth: '1px',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#9ca3af',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#3b82f6',
                                    borderWidth: '1.5px',
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </div>

                      {/* Clear Filters Button */}
                      {(candidateAddedFrom || candidateAddedTo || candidateTaggedJd) && (
                        <button
                          type="button"
                          onClick={() => {
                            setCandidateAddedFrom('');
                            setCandidateAddedTo('');
                            setCandidateTaggedJd(null);
                          }}
                          className="h-9 px-3 text-xs font-semibold text-gray-600 hover:text-red-600 bg-white hover:bg-red-50 border border-gray-300 hover:border-red-200 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer shadow-2xs"
                          title={t('bulkSchedule.clearFilters', 'Clear filters')}
                        >
                          <X size={13} />
                          <span>{t('bulkSchedule.clearFilters', 'Clear filters')}</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Candidates Table Area */}
                  {loadingCandidates ? null : candidates.length > 0 ? (
                    <div className="w-full flex-1 min-h-0 flex flex-col overflow-hidden">
                      {/* Unified Table Card like Job Applications */}
                      <div className="bg-white rounded-xl flex flex-1 min-h-0 flex-col shadow-2xs border border-gray-200/80 overflow-hidden">
                        {/* Table Card Subheader */}
                        <div className="px-3.5 py-2 bg-gray-50/70 border-b border-gray-200 flex items-center justify-between shrink-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-gray-700">
                              {t('bulkSchedule.candidates', 'Candidates')} ({totalCandidates})
                            </span>
                            {selectedCandidateIds.length > 0 && (
                              <span className="px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full">
                                {selectedCandidateIds.length} {t('bulkSchedule.selected', 'selected')}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={handleExportCSV}
                            disabled={isExporting || totalCandidates === 0}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors cursor-pointer disabled:opacity-50 shadow-2xs"
                            title={selectedCandidateIds.length === 0 ? t('bulkSchedule.selectCandidatesToExportTooltip', 'Select candidates using the checkboxes to export CSV') : `${t('bulkSchedule.exportCSV', 'Export CSV')} (${selectedCandidateIds.length})`}
                          >
                            {isExporting ? <CircularProgress size={12} sx={{ color: '#0ea5e9' }} /> : <Download size={13} className="text-gray-500" />}
                            <span>
                              {isExporting
                                ? t('bulkSchedule.exporting', 'Exporting...')
                                : selectedCandidateIds.length > 0
                                  ? `${t('bulkSchedule.exportCSV', 'Export CSV')} (${selectedCandidateIds.length})`
                                  : t('bulkSchedule.exportCSV', 'Export CSV')}
                            </span>
                          </button>
                        </div>

                        {/* DataGrid */}
                        <div className="w-full flex-1 min-h-0">
                          <DataGrid
                            rows={candidates}
                            columns={candidateColumns}
                            paginationModel={{ pageSize: pageSize, page: 0 }}
                            pageSizeOptions={[12, 25, 50, 100, 200, 500, 1000, 2000]}
                            hideFooter
                            sortingMode="server"
                            sortModel={candidateSortModel}
                            onSortModelChange={(model) => {
                              setCandidateSortModel(model);
                              setPage(1);
                            }}
                            checkboxSelection
                            rowSelectionModel={rowSelectionModel}
                            onRowSelectionModelChange={(newSelectionModel) => {
                              let newSelectedIds = [];
                              if (Array.isArray(newSelectionModel)) {
                                newSelectedIds = newSelectionModel;
                              } else if (newSelectionModel && typeof newSelectionModel === 'object') {
                                if (newSelectionModel.type === 'include') {
                                  newSelectedIds = Array.from(newSelectionModel.ids || []);
                                } else if (newSelectionModel.type === 'exclude') {
                                  const excludedIds = new Set(Array.from(newSelectionModel.ids || []));
                                  newSelectedIds = candidates
                                    .map((c) => c.id)
                                    .filter((id) => !excludedIds.has(id));
                                }
                              }
                              setSelectedCandidateIds(newSelectedIds);
                            }}
                            onCellClick={handleCellClick}
                            key={gridKey}
                            rowHeight={46}
                            columnHeaderHeight={40}
                            sx={{ ...dataGridSx, height: '100%' }}
                          />
                        </div>

                        {/* Pagination pinned flush at the bottom of the table card */}
                        <div className="bg-white border-t border-gray-200 z-10 rounded-b-xl shrink-0">
                          <Pagination
                            currentPage={page}
                            totalPages={totalPages}
                            totalCount={totalCandidates}
                            currentCount={candidates.length}
                            onPageChange={(newPage) => setPage(newPage)}
                            onPageSizeChange={updatePageSize}
                            pageSize={pageSize}
                            pageSizeOptions={pageSizeOptions}
                            showPageSize={true}
                            hasActiveFilters={Boolean(candidateAddedFrom || candidateAddedTo || candidateTaggedJd)}
                            forceVisible={true}
                            fixed={false}
                            className="py-0"
                          />
                        </div>
                      </div>

                      {/* Action buttons at bottom */}
                      <div className="mt-3 flex items-center justify-end gap-2 shrink-0">
                        <Button
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={handleCancel}
                          disabled={scheduling}
                          size="small"
                          sx={{
                            ...buttonSx,
                            color: '#64748b',
                            borderColor: '#d1d5db',
                            borderWidth: '1.5px',
                            px: 2.25,
                            py: 0.7,
                            fontSize: '0.8125rem',
                            borderRadius: '8px',
                            '&:hover': { borderColor: '#9ca3af', bgcolor: '#f8fafc', borderWidth: '1.5px' },
                          }}
                        >
                          {t('bulkSchedule.cancel')}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          endIcon={scheduling ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                          onClick={handleScheduleInterviews}
                          disabled={isScheduleButtonDisabled}
                          sx={{
                            ...buttonSx,
                            background: GRADIENT,
                            color: '#fff',
                            px: 2.5,
                            py: 0.7,
                            fontSize: '0.8125rem',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px -1px rgba(14, 165, 233, 0.35)',
                            '&:hover': { background: GRADIENT, opacity: 0.92, boxShadow: '0 4px 12px -1px rgba(14, 165, 233, 0.45)' },
                            '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' },
                          }}
                        >
                          {scheduling ? t('bulkSchedule.scheduling') : t('bulkSchedule.scheduleInterviews')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Alert severity="info" sx={{ mt: 1.5, borderRadius: '8px', ...FONT, fontSize: '0.8125rem' }}>
                      {t('bulkSchedule.noCandidatesFound')}
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
      </div>
      </PageCard>

      {/* Modals for Add JD */}
      <AddJobDescriptionDialog
        open={showJobDescriptionModal}
        onClose={() => { setShowJobDescriptionModal(false); setJobDescriptionForm(initialFormState); setJobDescriptionError(""); }}
        onSubmit={handleJobDescriptionSubmit}
        jobDescriptionForm={jobDescriptionForm}
        handleInputChange={(e) => setJobDescriptionForm(prev => ({ ...prev, [e.target.name]: e.target.value }))}
        departments={departments}
        selectedJobDescription={null}
        jobDescriptionError={jobDescriptionError}
        setShowAddDepartmentModal={setShowAddDepartmentModal}
      />

      <AddDepartmentModal
        open={showAddDepartmentModal}
        onClose={() => { setShowAddDepartmentModal(false); setDepartmentError(""); setDepartmentSuccess(""); }}
        onSubmit={handleAddDepartment}
        departmentName={newDepartmentName}
        setDepartmentName={setNewDepartmentName}
        departmentError={departmentError}
        departmentSuccess={departmentSuccess}
      />

      <JDFromAudioModal
        open={showAudioModal}
        onClose={() => setShowAudioModal(false)}
        onApply={handleAudioJDApply}
        initialAuditId={null}
      />

      {/* Side Popup Drawer: Define Focus Areas & Interview Configuration */}
      <Drawer
        anchor={isRTL ? 'left' : 'right'}
        open={Boolean(showFocusAreaDrawer)}
        onClose={() => setShowFocusAreaDrawer(false)}
        transitionDuration={{ enter: 350, exit: 250 }}
        SlideProps={{
          easing: {
            enter: 'cubic-bezier(0.16, 1, 0.3, 1)',
            exit: 'cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
        sx={{
          zIndex: 1350,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(2px)',
          },
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: '480px', md: '520px' },
            maxWidth: '100%',
            boxSizing: 'border-box',
            background: '#ffffff',
            boxShadow: isRTL
              ? '10px 0 40px rgba(0, 0, 0, 0.18)'
              : '-10px 0 40px rgba(0, 0, 0, 0.18)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: FONT.fontFamily,
          },
        }}
      >
        {/* Drawer Header with Blue-Purple Gradient */}
        <div
          className="flex items-center justify-between px-6 py-4 shadow-sm shrink-0"
          style={{ background: GRADIENT }}
        >
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-xs flex items-center justify-center text-white shrink-0">
              <Sliders size={19} className="text-white" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white tracking-tight leading-tight">
                {t('bulkSchedule.defineSkillsStep', 'Define Interview Focus Areas')}
              </h2>
              <p className="text-[11px] text-blue-100 line-clamp-1">
                {selectedJdObject?.title ? `${selectedJdObject.title} • ` : ''}
                {t('bulkSchedule.defineFocusAreasSubtitle', 'Configure skills and interview parameters')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowFocusAreaDrawer(false)}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors cursor-pointer"
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Drawer Body - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* 1. Required Skills */}
          <Box>
            <Typography sx={sectionTitleSx}>
              {t('bulkSchedule.requiredSkills')}
            </Typography>
            <div className="flex flex-wrap gap-2">
              {requiredSkills.length > 0 ? (
                requiredSkills.map((skill, index) => (
                  <SkillToggleChip
                    key={index}
                    skill={skill}
                    active={focusSkills.includes(skill)}
                    onToggle={(value) => (focusSkills.includes(value) ? handleDeleteSkill(value) : handleAddFocusSkill(value))}
                    idleBg="#fffbeb"
                    idleColor="#92400e"
                    idleBorder="#fde68a"
                    activeBg="linear-gradient(135deg, #f59e0b, #d97706)"
                    activeBorder="#d97706"
                  />
                ))
              ) : (
                <Typography sx={{ ...FONT, fontSize: '0.85rem', color: '#64748b' }}>
                  {t('bulkSchedule.noRequiredSkills')}
                </Typography>
              )}
            </div>
          </Box>

          {/* 2. Nice-to-Have Skills */}
          <Box>
            <Typography sx={sectionTitleSx}>
              {t('bulkSchedule.niceToHaveSkills')}
            </Typography>
            <div className="flex flex-wrap gap-2">
              {niceToHaveSkills.length > 0 ? (
                niceToHaveSkills.map((skill, index) => (
                  <SkillToggleChip
                    key={index}
                    skill={skill}
                    active={focusSkills.includes(skill)}
                    onToggle={(value) => (focusSkills.includes(value) ? handleDeleteSkill(value) : handleAddFocusSkill(value))}
                    idleBg="#f0f9ff"
                    idleColor="#0369a1"
                    idleBorder="#bae6fd"
                    activeBg="linear-gradient(135deg, #0ea5e9, #0284c7)"
                    activeBorder="#0284c7"
                  />
                ))
              ) : (
                <Typography sx={{ ...FONT, fontSize: '0.85rem', color: '#64748b' }}>
                  {t('bulkSchedule.noNiceToHaveSkills')}
                </Typography>
              )}
            </div>
          </Box>

          {/* 3. Additional Focus Skills (Manual Entry) */}
          <Box>
            <Typography sx={sectionTitleSx}>
              {t('bulkSchedule.additionalSkills')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
              <TextField
                fullWidth
                size="small"
                label={t('bulkSchedule.addSkillLabel')}
                variant="outlined"
                value={manualSkillInput}
                onChange={(e) => setManualSkillInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddManualSkill();
                  }
                }}
                InputLabelProps={{ sx: FONT }}
                InputProps={{ sx: outlinedInputSx }}
              />
              <Button
                variant="contained"
                onClick={handleAddManualSkill}
                disabled={!manualSkillInput.trim()}
                startIcon={<Plus size={16} />}
                sx={{
                  ...buttonSx,
                  minWidth: 120,
                  height: 40,
                  px: 2,
                  background: GRADIENT,
                  color: '#fff',
                  boxShadow: 'none',
                  '&:hover': { background: GRADIENT, opacity: 0.92, boxShadow: 'none' },
                  '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' },
                }}
              >
                {t('bulkSchedule.addSkillLabel')}
              </Button>
            </Box>
          </Box>

          {/* 4. All Focus Skills */}
          <Box sx={{ p: 2.5, borderRadius: '14px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ ...sectionTitleSx, mb: 0 }}>
                {t('bulkSchedule.allFocusSkills')} ({focusSkills.length})
              </Typography>
              {focusSkills.length > 0 && (
                <button
                  type="button"
                  onClick={() => setFocusSkills([])}
                  className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors cursor-pointer"
                >
                  {t('bulkSchedule.clearAll', 'Clear all')}
                </button>
              )}
            </Box>
            <div className="flex flex-wrap gap-2 min-h-[38px] items-center">
              {focusSkills.length > 0 ? (
                focusSkills.map((skill, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-xl shadow-xs"
                    style={{
                      fontFamily: FONT.fontFamily,
                      background: GRADIENT,
                    }}
                  >
                    {skill}
                    <button
                      type="button"
                      onClick={() => handleDeleteSkill(skill)}
                      className="p-0.5 hover:bg-white/20 rounded-full cursor-pointer transition-colors"
                      title="Remove skill"
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))
              ) : (
                <Typography sx={{ ...FONT, fontSize: '0.8125rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  {t('bulkSchedule.noFocusSkills')}
                </Typography>
              )}
            </div>
          </Box>

          {/* 5. Interview Parameters Configuration */}
          <Box sx={{ pt: 2, borderTop: '1px solid #e2e8f0' }}>
            <Typography sx={{ ...sectionTitleSx, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Sliders size={17} className="text-indigo-600" />
              {t('bulkSchedule.interviewParameters', 'Interview Configuration')}
            </Typography>
            <Typography sx={{ ...FONT, fontSize: '0.775rem', color: '#64748b', mb: 2 }}>
              {t('bulkSchedule.interviewParametersDesc', 'Set the questions limit and total duration for all candidates in this bulk scheduling.')}
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                p: 2.5,
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)'
              }}
            >
              {/* Number of Questions */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <HelpCircle size={15} className="text-indigo-600" />
                  <Typography sx={{ ...FONT, fontWeight: 600, fontSize: '0.8125rem', color: '#1e293b' }}>
                    {t('bulkSchedule.numberOfQuestions', 'Number of Questions')}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  value={maxNoOfQuestions}
                  error={isQuestionsError}
                  onKeyDown={(e) => {
                    if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = (e.clipboardData || window.clipboardData)?.getData('text') || '';
                    if (!/^\d+$/.test(pasted.trim())) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setMaxNoOfQuestions('');
                      return;
                    }
                    const digitsOnly = val.replace(/\D/g, '');
                    setMaxNoOfQuestions(digitsOnly);
                  }}
                  inputProps={{ min: 5, max: 100, step: 1 }}
                  placeholder={defaultQuestions != null ? `e.g., ${defaultQuestions}` : t('candidateDetails.inviteSchedule.unlimitedPlaceholder', 'Unlimited')}
                  helperText={
                    isQuestionsError
                      ? t('bulkSchedule.questionsMinError', 'Minimum number of questions must be at least 5')
                      : (defaultQuestions != null
                          ? t('bulkSchedule.numberOfQuestionsHelp', `Total questions to ask (min: 5, default: ${defaultQuestions}, leave blank for default)`)
                          : t('bulkSchedule.numberOfQuestionsHelpUnlimited', 'Total questions to ask (default: Unlimited, min: 5, leave blank for unlimited)'))
                  }
                  FormHelperTextProps={{
                    sx: {
                      ...FONT,
                      fontSize: '0.725rem',
                      color: isQuestionsError ? '#ef4444 !important' : '#64748b',
                      fontWeight: isQuestionsError ? 500 : 400
                    }
                  }}
                  InputLabelProps={{ sx: FONT }}
                  InputProps={{ sx: outlinedInputSx }}
                />
              </Box>

              {/* Total Duration (Minutes) */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Clock size={15} className="text-indigo-600" />
                  <Typography sx={{ ...FONT, fontWeight: 600, fontSize: '0.8125rem', color: '#1e293b' }}>
                    {t('bulkSchedule.interviewDuration', 'Interview Duration (Minutes)')}
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  value={interviewDurationMinutes}
                  error={isDurationError}
                  onKeyDown={(e) => {
                    if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = (e.clipboardData || window.clipboardData)?.getData('text') || '';
                    if (!/^\d+$/.test(pasted.trim())) {
                      e.preventDefault();
                      return;
                    }
                    const num = parseInt(pasted.trim(), 10);
                    if (num > 120) {
                      e.preventDefault();
                      setInterviewDurationMinutes('120');
                    }
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setInterviewDurationMinutes('');
                      return;
                    }
                    const digitsOnly = val.replace(/\D/g, '');
                    if (digitsOnly === '') {
                      setInterviewDurationMinutes('');
                      return;
                    }
                    const num = parseInt(digitsOnly, 10);
                    if (num > 120) {
                      setInterviewDurationMinutes('120');
                    } else {
                      setInterviewDurationMinutes(digitsOnly);
                    }
                  }}
                  onBlur={() => {
                    if (interviewDurationMinutes !== '') {
                      const num = parseInt(interviewDurationMinutes, 10);
                      if (num > 120) {
                        setInterviewDurationMinutes('120');
                      }
                    }
                  }}
                  inputProps={{ min: 15, max: 120, step: 5 }}
                  placeholder={defaultDuration ? `e.g., ${defaultDuration}` : t('bulkSchedule.interviewDurationPlaceholder', 'e.g., 30')}
                  helperText={
                    isDurationError
                      ? t('bulkSchedule.durationRangeError', 'Duration must be between 15 and 120 minutes')
                      : t('bulkSchedule.interviewDurationHelp', `Total interview time in minutes (default: ${defaultDuration || 30} mins, 15 - 120 mins)`)
                  }
                  FormHelperTextProps={{
                    sx: {
                      ...FONT,
                      fontSize: '0.725rem',
                      color: isDurationError ? '#ef4444 !important' : '#64748b',
                      fontWeight: isDurationError ? 500 : 400
                    }
                  }}
                  InputLabelProps={{ sx: FONT }}
                  InputProps={{ sx: outlinedInputSx }}
                />
              </Box>
            </Box>
          </Box>
        </div>

        {/* Drawer Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white shrink-0">
          <button
            type="button"
            onClick={() => {
              setFocusSkills([]);
            }}
            className="px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
          >
            {t('bulkSchedule.clearFocusSkills', 'Clear Focus Skills')}
          </button>
          <button
            type="button"
            onClick={() => {
              if (maxNoOfQuestions !== '' && Number(maxNoOfQuestions) < 5) {
                toast.error(t('bulkSchedule.questionsMinError', 'Minimum number of questions must be at least 5'));
                return;
              }
              if (interviewDurationMinutes !== '' && (Number(interviewDurationMinutes) < 10 || Number(interviewDurationMinutes) > 180)) {
                toast.error(t('bulkSchedule.durationRangeError', 'Duration must be between 10 and 180 minutes'));
                return;
              }
              setShowFocusAreaDrawer(false);
            }}
            className="px-6 py-2 text-xs sm:text-sm font-semibold text-white rounded-lg transition-all shadow-sm hover:opacity-95 cursor-pointer"
            style={{ background: GRADIENT }}
          >
            {t('bulkSchedule.saveAndApply', 'Done')}
          </button>
        </div>
      </Drawer>
    </PageShell>
  );
};

export default BulkSchedulePage;
